import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useThemeColors } from '../theme/useThemeColors';
import { SERVER_BASE_URL } from '../config';

interface ScriptManagerProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
}

export const ScriptManager: React.FC<ScriptManagerProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const { themeColor, themeHoverColor } = useThemeColors();
  const [script, setScript] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async () => {
    if (!script.trim()) {
      setError('Please paste a script');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${SERVER_BASE_URL}/api/parse-script`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ script }),
      });
      if (!res.ok) {
        let msg = res.statusText;
        try {
          const data = await res.json();
          if (data?.error) msg = data.error;
        } catch {}
        throw new Error(msg);
      }
      const parsedScript = await res.json();
      if (typeof parsedScript.tease !== 'string' || typeof parsedScript.intro !== 'string' || !Array.isArray(parsedScript.songs) || typeof parsedScript.outro !== 'string') {
        throw new Error('Invalid script structure: Missing required keys or incorrect types.');
      }
      onSave(parsedScript);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse script');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600, textTransform: 'uppercase', fontSize: '1.5rem' }}>Upload Script</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Paste your full script below. The script will be parsed into an intro, individual song lines, and an outro.
          </Typography>
          <TextField
            multiline
            rows={10}
            fullWidth
            value={script}
            onChange={(e) => setScript(e.target.value)}
            placeholder="Paste your script here..."
            sx={{ mt: 2 }}
          />
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#000000' }}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{
            backgroundColor: themeColor,
            color: '#000000',
            '&:hover': {
              backgroundColor: themeHoverColor,
            },
          }}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export {}; 